import { getEvent, getEventConfirmedMembers, getEventInvitedMembers } from 'services/events';
import { create } from 'zustand';

interface EventStore {
    event: any;
    loading: boolean;
    images: any[];
    documents: any[];
    invite: boolean;
    getEvent: (id: string) => void;
    setEvent: (event: any) => void;
    setImages: (images: any[]) => void;
    setDocuments: (documents: any[]) => void;
    setInvite: (invite: boolean) => void;
}

export const useEventStore = create<EventStore>((set, get) => ({
    event: null,
    loading: false,
    invite: false,
    images: [],
    documents: [],
    getEvent: async (id: string) => {
        if (!get().event) set({ loading: true });
        try {
            const { data } = await getEvent(id);
            if (data.event) {
                let event = { ...data.event };
                
                // Fetch all confirmed members if there are more pages
                if (event.confirmed?.pageInfo?.hasNextPage) {
                    const allConfirmed = [...event.confirmed.edges];
                    let hasNextPage = event.confirmed.pageInfo.hasNextPage;
                    let endCursor = event.confirmed.pageInfo.endCursor;
                    
                    while (hasNextPage) {
                        const { data: confirmedData } = await getEventConfirmedMembers(id, endCursor);
                        if (confirmedData.event?.confirmed) {
                            allConfirmed.push(...confirmedData.event.confirmed.edges);
                            hasNextPage = confirmedData.event.confirmed.pageInfo.hasNextPage;
                            endCursor = confirmedData.event.confirmed.pageInfo.endCursor;
                        } else {
                            break;
                        }
                    }
                    
                    event.confirmed = {
                        ...event.confirmed,
                        edges: allConfirmed,
                        pageInfo: { hasNextPage: false, endCursor: null }
                    };
                }
                
                // Fetch all invited members if there are more pages
                if (event.invited?.pageInfo?.hasNextPage) {
                    const allInvited = [...event.invited.edges];
                    let hasNextPage = event.invited.pageInfo.hasNextPage;
                    let endCursor = event.invited.pageInfo.endCursor;
                    
                    while (hasNextPage) {
                        const { data: invitedData } = await getEventInvitedMembers(id, endCursor);
                        if (invitedData.event?.invited) {
                            allInvited.push(...invitedData.event.invited.edges);
                            hasNextPage = invitedData.event.invited.pageInfo.hasNextPage;
                            endCursor = invitedData.event.invited.pageInfo.endCursor;
                        } else {
                            break;
                        }
                    }
                    
                    event.invited = {
                        ...event.invited,
                        edges: allInvited,
                        pageInfo: { hasNextPage: false, endCursor: null }
                    };
                }
                
                set({ event });
            }
        } catch (error) {
            console.error('Error loading event:', error);
        } finally {
            set({ loading: false });
        }
    },
    setEvent: (ev: any) => {
        set({ event: ev });
    },
    setImages: (images: any[]) => {
        set({ images });
    },
    setDocuments: (documents: any[]) => {
        set({ documents });
    },
    setInvite: (invite: boolean) => {
        set({ invite });
    },
}));
